import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { loginSchema, type LoginInput } from "@platform/validation";
import { Button, Input, Label } from "@nthamizh/ui";
import { useLogin, useResendVerification } from "@platform/hooks";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useAuthStore } from "../stores/authStore";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import type { AuthSession } from "@platform/types";
import { ApiError } from "@platform/api-client";
import { resolvePostLoginUrl, isEnterpriseSubdomain } from "../lib/subdomain";
import { useState, useRef } from "react";
import { ArrowRight, Building2 } from "lucide-react";
import { useApiClient } from "@platform/hooks";

interface SsoInfo {
  providerName: string;
  protocol: string;
  initiateUrl: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setSession = useAuthStore((s) => s.setSession);
  const login = useLogin();
  const resendVerification = useResendVerification();
  const client = useApiClient();

  const [ssoInfo, setSsoInfo] = useState<SsoInfo | null>(null);
  const [ssoChecking, setSsoChecking] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const lastCheckedEmail = useRef("");

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<LoginInput>({ resolver: zodResolver(loginSchema) });


  // On enterprise subdomain — check if this email triggers SSO
  const handleEmailBlur = async () => {
    if (!isEnterpriseSubdomain()) return;
    const email = getValues("email")?.trim();
    if (!email || !email.includes("@") || email === lastCheckedEmail.current) return;
    lastCheckedEmail.current = email;

    const slug = window.location.hostname.split(".")[0] ?? "";
    setSsoChecking(true);
    setSsoInfo(null);
    try {
      const result = await client.sso.checkSso(email, slug);
      if (result.ssoEnabled && result.providerName && result.initiateUrl) {
        setSsoInfo({
          providerName: result.providerName,
          protocol: result.protocol ?? "oidc",
          initiateUrl: result.initiateUrl,
        });
      }
    } catch {
      // SSO check failed silently — fall back to password
    } finally {
      setSsoChecking(false);
    }
  };

  const handleSsoRedirect = () => {
    if (!ssoInfo) return;
    // Build the full API URL for SSO initiation
    const apiBase = import.meta.env.VITE_API_URL ?? "http://localhost:4000";
    window.location.assign(`${apiBase}${ssoInfo.initiateUrl}`);
  };

  const onSubmit = handleSubmit(async (values) => {
    setResendSent(false);
    const session = (await login.mutateAsync(values)) as AuthSession;
    setSession(session);

    const destination = resolvePostLoginUrl({
      platformRole: session.user.platformRole ?? null,
      defaultEnterpriseSlug: (session.user as unknown as { defaultEnterpriseSlug?: string }).defaultEnterpriseSlug ?? null,
      accessToken: session.accessToken,
    });

    if (destination.startsWith("http")) {
      const redirect = (url: string) => { window.location.assign(url); };
      redirect(destination);
    } else {
      navigate(destination, { replace: true });
    }
  });

  const idleLogout = searchParams.get("reason") === "idle";
  const handoffFailed = searchParams.get("reason") === "handoff_failed";

  // Enterprise subdomain — show enterprise branding
  const onEnterpriseSub = isEnterpriseSubdomain();
  const enterpriseSlug = onEnterpriseSub ? window.location.hostname.split(".")[0] : null;

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
      <div className="w-full max-w-[380px]">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img src="/logo.png" alt="Tailseed" className="h-16 w-16 object-contain" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900">Tailseed</h1>
          <p className="mt-1 text-sm text-gray-500">
            {onEnterpriseSub
              ? `Sign in to ${enterpriseSlug}`
              : "Sign in to your workspace"}
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-[0_1px_3px_0_rgb(0_0_0/0.06)] p-6">
          {idleLogout && (
            <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-sm text-amber-800">
              You were signed out after 15 minutes of inactivity.
            </div>
          )}
          {handoffFailed && (
            <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700">
              Could not load your enterprise workspace. Please sign in again.
            </div>
          )}

          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">Work email</Label>
              <Input
                id="email" type="email"
                placeholder={onEnterpriseSub ? `you@${enterpriseSlug}.com` : "you@example.com"}
                autoComplete="email"
                {...register("email")}
                onBlur={handleEmailBlur}
                error={errors.email?.message}
              />
            </div>

            {/* SSO detection result */}
            {ssoChecking && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-500">
                <div className="w-3.5 h-3.5 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin flex-shrink-0" />
                Checking sign-in options…
              </div>
            )}

            {ssoInfo && !ssoChecking && (
              <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-3">
                <div className="flex items-center gap-2.5 mb-2.5">
                  <Building2 className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-indigo-800">SSO enabled</p>
                    <p className="text-xs text-indigo-600">
                      Sign in with {ssoInfo.providerName}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleSsoRedirect}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
                >
                  Continue with {ssoInfo.providerName}
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setSsoInfo(null)}
                  className="w-full mt-2 text-xs text-indigo-400 hover:text-indigo-600 text-center"
                >
                  Use email + password instead
                </button>
              </div>
            )}

            {/* Password field — hidden when SSO is active */}
            {!ssoInfo && (
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link to="/forgot-password" className="text-xs font-medium text-blue-600 hover:text-blue-700">
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password" type="password"
                  autoComplete="current-password" {...register("password")}
                  error={errors.password?.message}
                />
              </div>
            )}

            {login.isError && !ssoInfo && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700">
                {login.error instanceof ApiError ? login.error.message : "Invalid email or password."}
                {login.error instanceof ApiError && login.error.code === "EMAIL_NOT_VERIFIED" && (
                  <div className="mt-1.5">
                    {resendSent ? (
                      <span className="text-red-600">Verification email sent — check your inbox.</span>
                    ) : (
                      <button
                        type="button"
                        onClick={async () => {
                          const email = getValues("email")?.trim();
                          if (!email) return;
                          await resendVerification.mutateAsync(email);
                          setResendSent(true);
                        }}
                        disabled={resendVerification.isPending}
                        className="font-medium underline hover:no-underline disabled:opacity-50"
                      >
                        {resendVerification.isPending ? "Sending…" : "Resend verification email"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {!ssoInfo && (
              <Button type="submit" loading={login.isPending} className="w-full mt-1">
                Sign in
              </Button>
            )}
          </form>

          {!ssoInfo && (
            <>
              <div className="my-5 flex items-center gap-3">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-xs text-gray-400">or</span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>
              <GoogleSignInButton />
            </>
          )}

          <p className="mt-5 text-center text-sm text-gray-500">
            No account?{" "}
            <Link to="/register" className="font-medium text-blue-600 hover:text-blue-700">
              Register
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
